<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import { session } from '../session'

const { t } = useI18n()
const isAdmin = computed(() => session.user?.role === 'ADMIN')
const users = ref([])
const error = ref('')
const loading = ref(true)
const saving = ref(false)
const pendingUserId = ref(null)
const deletingUserId = ref(null)
const form = ref({ username: '', password: '', role: 'MANAGER' })

async function load() {
  loading.value = true
  try {
    users.value = (await api('/admin/users')).users
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

async function createUser() {
  error.value = ''
  saving.value = true
  try {
    const result = await api('/admin/users', { method: 'POST', body: JSON.stringify(form.value) })
    users.value = [...users.value, result.user].sort((left, right) => left.username.localeCompare(right.username))
    form.value = { username: '', password: '', role: 'MANAGER' }
  } catch (cause) {
    error.value = cause.message
  } finally {
    saving.value = false
  }
}

function roleLabel(role) {
  return role === 'ADMIN' ? t('administrator') : t('manager')
}

function statusLabel(user) {
  if (!user.enabled) return t('userBlocked')
  return user.mustChangePassword ? t('passwordChangeRequired') : t('userActive')
}

function isCurrentUser(user) {
  return user.id === session.user?.id
}

function replaceUser(updated) {
  users.value = users.value.map((user) => (user.id === updated.id ? updated : user))
}

async function toggleUserEnabled(user) {
  const enabled = !user.enabled
  const confirmationKey = enabled ? 'unblockUserConfirm' : 'blockUserConfirm'
  if (!window.confirm(t(confirmationKey, { name: user.username }))) return

  error.value = ''
  pendingUserId.value = user.id
  try {
    const result = await api(`/admin/users/${user.id}/enabled`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    })
    replaceUser(result.user)
  } catch (cause) {
    error.value = cause.message
  } finally {
    pendingUserId.value = null
  }
}

async function deleteUser(user) {
  if (!window.confirm(t('deleteUserConfirm', { name: user.username }))) return

  error.value = ''
  deletingUserId.value = user.id
  try {
    await api(`/admin/users/${user.id}`, { method: 'DELETE' })
    users.value = users.value.filter((item) => item.id !== user.id)
  } catch (cause) {
    error.value = cause.message
  } finally {
    deletingUserId.value = null
  }
}

onMounted(load)
</script>

<template>
  <section>
    <div class="page-title"><div><p class="eyebrow">{{ t('settings') }}</p><h1>{{ t('usersTitle') }}</h1></div></div>
    <p v-if="!isAdmin" class="notice info" role="status">{{ t('viewOnlyHint') }}</p>
    <div :class="isAdmin ? 'two-columns' : 'single-column'">
      <section v-if="isAdmin" class="card">
        <h2>{{ t('createUser') }}</h2>
        <p class="muted">{{ t('newUserHint') }}</p>
        <form class="form-stack" @submit.prevent="createUser">
          <label>{{ t('username') }} <input v-model="form.username" pattern="[A-Za-z0-9._-]{3,64}" maxlength="64" required /></label>
          <label>{{ t('initialPassword') }} <input v-model="form.password" type="password" minlength="10" required /></label>
          <label>{{ t('role') }} <select v-model="form.role"><option value="MANAGER">{{ t('manager') }}</option><option value="ADMIN">{{ t('administrator') }}</option></select></label>
          <button class="button" :disabled="saving" type="submit">{{ saving ? t('creating') : t('create') }}</button>
        </form>
      </section>
      <section class="card">
        <h2>{{ t('existingUsers') }}</h2>
        <p v-if="error" class="notice error" role="alert">{{ error }}</p>
        <p v-if="loading" class="muted">{{ t('loading') }}</p>
        <div v-else class="table-wrap"><table><thead><tr><th>{{ t('username') }}</th><th>{{ t('role') }}</th><th>{{ t('userStatus') }}</th><th v-if="isAdmin">{{ t('actions') }}</th></tr></thead><tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.username }}</td>
            <td>{{ roleLabel(user.role) }}</td>
            <td>{{ statusLabel(user) }}</td>
            <td v-if="isAdmin">
              <span v-if="isCurrentUser(user)" class="muted">{{ t('currentUser') }}</span>
              <div v-else class="user-actions">
                <button class="button button-secondary button-small" :disabled="pendingUserId !== null || deletingUserId !== null" type="button" @click="toggleUserEnabled(user)">
                  {{ pendingUserId === user.id ? (user.enabled ? t('blockingUser') : t('unblockingUser')) : (user.enabled ? t('blockUser') : t('unblockUser')) }}
                </button>
                <button class="button button-danger button-small" :disabled="pendingUserId !== null || deletingUserId !== null" type="button" @click="deleteUser(user)">
                  {{ deletingUserId === user.id ? t('deletingUser') : t('deleteUser') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody></table></div>
      </section>
    </div>
  </section>
</template>
